import { supabase } from '../lib/supabase';
import {
  BacklogTask,
  Dimension,
  DimensionKind,
  Review,
  ReviewStatus,
  UserDoc,
  DIMENSION_KINDS,
  DIMENSION_LABELS,
  STATUSES,
  PROYECTO_STATUSES,
  TIPOS,
  URGENCIAS,
  DIFICULTADES,
} from '../domain/types';

function unwrap<T>(res: { data: T | null; error: any }): T {
  if (res.error) throw new Error(res.error.message || 'Error de Supabase.');
  return res.data as T;
}

/** Llama una función RPC con un único parámetro jsonb `p`. */
async function rpc<T = any>(fn: string, p: Record<string, unknown>): Promise<T> {
  return unwrap(await supabase.rpc(fn, { p }));
}

/** Invoca la Edge Function admin-users. Devuelve 200 siempre; el error real viaja en `ok:false`. */
async function invokeAdminUsers(action: 'upsert' | 'remove', payload: Record<string, unknown>): Promise<any> {
  const { data, error } = await supabase.functions.invoke('admin-users', { body: { action, ...payload } });
  if (error) throw new Error(error.message || 'No se pudo contactar la función de usuarios.');
  if (!data || data.ok !== true) throw new Error((data && data.error) || 'No se pudo completar la operación.');
  return data;
}

const num = (v: any): number | '' => (v === null || v === undefined || v === '' ? '' : Number(v));
const str = (v: any): string => (v == null ? '' : String(v));

function mapTask(d: any): BacklogTask {
  return {
    id: d.id,
    espacio: str(d.espacio), cliente_interno: str(d.cliente_interno),
    direccion: str(d.direccion), proyecto: str(d.proyecto), categoria: str(d.categoria),
    pieza: str(d.pieza), sede: str(d.sede),
    tipo: str(d.tipo), urgencia: str(d.urgencia), dificultad: str(d.dificultad),
    horas_estimadas: num(d.horas_estimadas), horas_reales: num(d.horas_reales),
    ajuste: str(d.ajuste), ajustes_count: Number(d.ajustes_count) || 0,
    description: str(d.description), titulo: str(d.titulo), enlace_documentos: str(d.enlace_documentos),
    status: d.status, rejected_reason: str(d.rejected_reason),
    fecha_solicitud: str(d.fecha_solicitud) || str(d.created_at),
    created_at: str(d.created_at), created_by: str(d.created_by),
    assigned_to: str(d.assigned_to), approved_by: str(d.approved_by), approved_at: str(d.approved_at),
    start_date: str(d.start_date), deadline: str(d.deadline), finished_at: str(d.finished_at),
    updated_at: str(d.updated_at), updated_by: str(d.updated_by),
  };
}

function mapReview(d: any): Review {
  return {
    id: d.id,
    task_id: str(d.task_id),
    status: d.status,
    valoracion: str(d.valoracion),
    descripcion: str(d.descripcion),
    reviewed_by: str(d.reviewed_by),
    reviewed_at: str(d.reviewed_at),
    created_at: str(d.created_at),
    updated_at: str(d.updated_at),
  };
}

async function fetchDimensions(activeOnly: boolean): Promise<Record<string, Dimension[]>> {
  const out: Record<string, Dimension[]> = {};
  await Promise.all(
    DIMENSION_KINDS.map(async (k) => {
      let q = supabase.from(k).select('*').order('name');
      if (activeOnly) q = q.eq('active', true);
      out[k] = unwrap(await q) as Dimension[];
    })
  );
  return out;
}

async function currentProfile(): Promise<UserDoc> {
  const { data: au } = await supabase.auth.getUser();
  if (!au.user) throw new Error('No autenticado.');
  const email = (au.user.email || '').toLowerCase();
  const prof = unwrap(
    await supabase.from('users').select('*').eq('email', email).single()
  ) as any;
  return { email: prof.email, first_name: prof.first_name, last_name: prof.last_name, role: prof.role };
}

export const supabaseApi: any = {
  bootstrap: async () => {
    const me = await currentProfile();
    const dimensions = await fetchDimensions(true);
    // Espacios a cargo del usuario actual. RLS deja leer las filas propias.
    // Resiliente: si la migración aún no está aplicada, no romper la app.
    let myEspacios: string[] = [];
    try {
      const rows = unwrap(
        await supabase.from('user_espacio').select('espacio').ilike('user_email', me.email)
      ) as any[];
      myEspacios = rows.map((r) => str(r.espacio)).sort((a, b) => a.localeCompare(b));
    } catch { myEspacios = []; }
    return {
      ok: true, me: me.email, role: me.role, myEspacios,
      statuses: [...STATUSES], proyectoStatuses: [...PROYECTO_STATUSES], tipos: [...TIPOS],
      urgencias: [...URGENCIAS], dificultades: [...DIFICULTADES],
      dimensionKinds: [...DIMENSION_KINDS], dimensionLabels: DIMENSION_LABELS, dimensions,
    };
  },
  syncMyClaims: async () => {
    try { const me = await currentProfile(); return { role: me.role }; } catch { return { role: null }; }
  },
  initFirstAdmin: async () => rpc('init_first_admin', {}),

  listAccessRoster: async () => unwrap(await supabase.from('users').select('*').order('email')) as UserDoc[],
  listAssignableUsers: async () => unwrap(await supabase.from('users').select('*').order('email')) as UserDoc[],
  upsertUser: async (u: Partial<UserDoc>) => rpc('upsert_user', u as any),
  setRole: async (p: { email: string; role: string }) => rpc('set_role', p),
  removeUser: async (p: { email: string }) => rpc('remove_user', p),
  // Alta/baja completas (roster + cuenta de login) vía Edge Function con service role.
  adminUpsertUser: async (u: Partial<UserDoc>) => invokeAdminUsers('upsert', u),
  adminRemoveUser: async (p: { email: string }) => invokeAdminUsers('remove', p),

  // Espacios a cargo de cada responsable. Devuelve email -> lista de espacios.
  listUserEspacios: async (): Promise<Record<string, string[]>> => {
    const rows = unwrap(await supabase.from('user_espacio').select('user_email, espacio')) as any[];
    const out: Record<string, string[]> = {};
    for (const r of rows) {
      const em = str(r.user_email).toLowerCase();
      (out[em] ||= []).push(str(r.espacio));
    }
    for (const em of Object.keys(out)) out[em].sort((a, b) => a.localeCompare(b));
    return out;
  },
  setUserEspacios: async (p: { email: string; espacios: string[] }) =>
    rpc('set_user_espacios', { email: p.email, espacios: p.espacios }),

  listAllDimensions: async (p?: { activeOnly?: boolean }) => fetchDimensions(p?.activeOnly !== false),
  upsertDimension: async (p: { kind: DimensionKind; payload: Record<string, unknown> }) => rpc('upsert_dimension', p),
  deactivateDimension: async (p: { kind: DimensionKind; id: string }) => rpc('deactivate_dimension', p),

  createTask: async (payload: Record<string, unknown>) => rpc('create_task', payload),
  updateTask: async (p: { id: string; updates: Record<string, unknown> }) => rpc('update_task', p),
  approveTask: async (p: { id: string; edits?: Record<string, unknown> }) => rpc('approve_task', p),
  rejectTask: async (p: { id: string; reason: string }) => rpc('reject_task', p),

  listComments: async (taskId: string) =>
    (unwrap(
      await supabase.from('task_comments').select('*').eq('task_id', taskId).order('created_at', { ascending: true })
    ) as any[]).map((d) => ({
      id: d.id, task_id: d.task_id, author_email: str(d.author_email), body: str(d.body), created_at: str(d.created_at),
    })),
  addComment: async (p: { taskId: string; body: string }) => rpc('add_task_comment', { task_id: p.taskId, body: p.body }),

  listReviews: async (p?: { status?: ReviewStatus }) => {
    let q = supabase.from('review').select('*').order('updated_at', { ascending: false });
    if (p?.status) q = q.eq('status', p.status);
    return (unwrap(await q) as any[]).map(mapReview);
  },
  submitReview: async (p: { task_id: string; valoracion: string; descripcion: string }) =>
    rpc('submit_review', p),

  seedTaxonomy: async () => ({ ok: true, counts: {} }), // el seed se aplica vía SQL
  runDeadlineCheck: async () => {
    const { data, error } = await supabase.rpc('process_deadlines');
    if (error) throw new Error(error.message || 'Error de Supabase.');
    return { ok: true, warned: Number(data) || 0 };
  },
};

export async function supabaseFetchBacklog(role: string, email: string): Promise<BacklogTask[]> {
  let q = supabase.from('backlog').select('*').order('created_at', { ascending: false });
  if (role === 'user') q = q.eq('assigned_to', email);
  const rows = unwrap(await q) as any[];
  return rows.map(mapTask);
}

export async function supabaseFetchTask(id: string): Promise<BacklogTask | null> {
  const { data, error } = await supabase.from('backlog').select('*').eq('id', id).single();
  if (error) return null;
  return mapTask(data);
}

export async function supabaseFetchAudit(id: string) {
  const rows = unwrap(
    await supabase.from('audit_log').select('*').eq('entity_id', id).order('ts', { ascending: false })
  ) as any[];
  return rows.map((d) => ({
    timestamp: str(d.ts), action: str(d.action), field: str(d.field),
    old_value: str(d.old_value), new_value: str(d.new_value), by: str(d.by),
  }));
}
